import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { ConfigService } from '@ddt-web/config-layer';
import { AppStoreConfig, DataServiceApiConstants, PasFeature, PasRcResolverService, PortfolioAnalysisParams, PositionDetails } from '@ddt-web/data-service';
import { AdditionalAnalysisParams } from '@ddt-web/domain';
import { AssetResolveResponse, PasApiService } from '@ddt-web/pas';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { SecurityResolutionDetailsRequest, WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as uniqBy from 'lodash/uniqBy';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { PortfolioPositionsUtil } from '../../../utils/portfolio-positions.util';
import { PasCustomParams } from '../../models/pas-processing.interface';
import { PasApiParamsService } from '../pas-api-params.service';
import { PasDataPortfolioAnalysisService } from '../pas-data-portfolio-analysis.service';
import { PasApiProcessingService } from './pas-api-processing.service';

/**
 * Service that processes the PAS APIs covering security-details API call for transient analysis workflows/ applications (IQ).
 * If this is the behavior needed, the override must be added in the app.module.ts:
 * {
 *    provide: PasApiProcessingService,
 *    useClass: PasTransientApiProcessingService
 * }
 *
 * This supports:
 * - building the request body (if not provided)
 * - making the API call
 * - error handling for security-details - if all securities fail for transient portfolio
 *    - throwSecurityDetailsPartialPortfoliosErrors is not used because there is only one portfolio analysed (transient portfolio)
 */
@Injectable()
export class PasTransientApiProcessingService extends PasApiProcessingService {

	constructor(
		protected dataPortfolioAnalysisService: PasDataPortfolioAnalysisService,
		protected apiParamService: PasApiParamsService, // can move the method to the normal API service
		protected storeService: StoreService,
		protected pasApiService: PasApiService,
		protected configService: ConfigService,
		protected applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		protected pasRcResolver: PasRcResolverService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) {
		super(
			dataPortfolioAnalysisService,
			apiParamService,
			storeService,
			pasApiService,
			configService,
			applicationStateStoreProxyService,
			pasRcResolver,
			storeConfig
		);
	}

	public fetchSecurityDetailsData(
		body: SecurityResolutionDetailsRequest,
		associatedPortfoliosResult: any,
		throwSecurityDetailsPartialPortfoliosErrors: boolean, // not used and not needed for transient analysis (only 1 transient portfolio analysed)
		additionalAnalysisParams?: AdditionalAnalysisParams,
		feature?: PasFeature,
		ids?: string[],
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): Observable<AssetResolveResponse> {
		let rc = DataServiceApiConstants[positionDetailsType] ?? DataServiceApiConstants.SECURITY_DETAILS;
		const storeName = PositionDetails.SECURITY_DETAILS_STORE_MAPPING[positionDetailsType] || PasApiService.POSITION_DETAILS_CONFIG_NAME;

		if (Object.values(PasFeature).includes(feature)) {
			rc = this.pasRcResolver.getRcForPasFeature(rc, feature);
		}

		return this.pasRcResolver.getRcForUserType(rc)
			.pipe(
				map((rc: string) => ({ rc, body })),
				switchMap((requestParams: WweSecurityDetailsParams) =>
					this.pasApiService.fetchSecurityDetails({
						date: additionalAnalysisParams?.date, ...requestParams
					}, feature, storeName)),
				map((response: AssetResolveResponse) => {
					if (response.errorCodes && isEmpty(response?.assets)) { // everything failed
						const configName = feature === PasFeature.COMPARE ? PasApiService.COMPARE_CONFIG_NAME : storeName;
						this.handleSecurityDetailsPartialErrors(response, configName);
					}
					return response;
				})
			);
	}

	public getPortfolioAnalysisParam(
		ids: any[],
		modelsConfig: any,
		associatedPortfolios: any,
		additionalAnalysisParams: AdditionalAnalysisParams,
		currency: string,
		pasCustomParams?: PasCustomParams,
		isBenchmarkPerPortfolio?: boolean
	): Observable<PortfolioAnalysisParams>[] {
		return [].concat(this.apiParamService.flattenPortfolios(ids, this.storeConfig, true).pipe(first(), map(portfolioParams => {
			if (!currency) {
				throw new Error('Invalid currency found in the url queryParams');
			}

			let allPortfoliosPosition = portfolioParams.map(portfolio => portfolio['positions']).reduce((acc, value) => acc.concat(value), []);
			allPortfoliosPosition = additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId ? // we need unique positions based on the additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId if provided
				uniqBy(allPortfoliosPosition, additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId) : allPortfoliosPosition;

			const positions = PortfolioPositionsUtil.createWweParamPositions(allPortfoliosPosition, '', additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId)
				.filter(position => Object.values(position.identifiers).some(value => !isNil(value)));

			const uniquePositions = uniqBy(positions, 'id');
			const uniqueWeight = 100 / uniquePositions.length;

			uniquePositions.forEach(position => {
				position.weight = uniqueWeight;
				position.value = undefined; // removing value - not valid anymore
			});
			return new PortfolioAnalysisParams(pasCustomParams?.transientAnalysisId, null, uniquePositions, null, currency);
		})));
	}
}
